<template>
  <div class="workflow-run-browser">
    <aside class="workflow-run-browser__history">
      <div class="workflow-run-browser__header">
        <div class="text-overline mb-1">Run History</div>
        <v-progress-circular
          v-if="isLoadingHistory"
          density="compact"
          size="18"
          width="2"
          indeterminate
          color="primary"
        />
      </div>

      <v-list class="workflow-run-browser__list" density="compact" bg-color="transparent">
        <v-list-item
          v-if="hasLiveDisplay"
          :active="selectedSource === 'live'"
          density="compact"
          class="workflow-run-browser__item"
          @click="selectLiveRun"
        >
          <template #title>
            <div class="workflow-run-browser__title">
              <span>{{ liveRunTitle }}</span>
              <v-chip size="x-small" density="compact" :color="statusColor(liveRunState.status)" class="ml-2">{{ liveRowLabel }}</v-chip>
            </div>
          </template>
          <template #subtitle>
            <span>{{ liveRunSubtitle }}</span>
          </template>
        </v-list-item>

        <v-list-item
          v-for="run in historicalRuns"
          :key="run.runId"
          :active="selectedSource === 'history' && selectedRunId === run.runId"
          density="compact"
          class="workflow-run-browser__item"
          @click="selectHistoricalRun(run.runId)"
        >
          <template #title>
            <div class="workflow-run-browser__title">
              <span>{{ formatRunTitle(run) }}</span>
              <v-chip size="x-small" density="compact" :color="statusColor(run.status)" class="ml-2">
                {{ run.status }}
              </v-chip>
            </div>
          </template>
          <template #subtitle>
            <span>{{ formatRunSubtitle(run) }}</span>
          </template>
        </v-list-item>

        <div
          v-if="!hasLiveDisplay && historicalRuns.length === 0 && !isLoadingHistory"
          class="workflow-run-browser__empty text-medium-emphasis"
        >
          No runs yet.
        </div>
      </v-list>
    </aside>

    <section class="workflow-run-browser__details">
      <FactoryRunPanel
        :run="selectedDisplayRun"
        @pause="$emit('pause')"
        @resume="$emit('resume')"
        @cancel="$emit('cancel')"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import FactoryRunPanel from '@/components/factories/FactoryRunPanel.vue'
import type { FactoryStreamState } from '@/composables/useFactoryStream'
import type { FactoryRunDisplay, FactoryRunRecord } from '@/types/factories'
import { toErroredFactoryRunDisplay, toHistoricalFactoryRunDisplay, toIdleFactoryRunDisplay } from '@/utils/factoryRuns'

const props = defineProps<{
  factoryId: string
  factoryName: string
  liveRunState: FactoryStreamState
}>()

defineEmits<{
  (e: 'pause'): void
  (e: 'resume'): void
  (e: 'cancel'): void
}>()

const store = useStore()
const selectedRunId = ref<string | null>(null)
const selectedSource = ref<'live' | 'history'>('history')

const hasLiveDisplay = computed<boolean>(() => (
  Boolean(props.liveRunState.runId) || props.liveRunState.status === 'error'
))

const historicalRuns = computed<FactoryRunRecord[]>(() => {
  const runs = store.getters.factoryRunsByFactory?.(props.factoryId) || []
  return runs.filter((run: FactoryRunRecord) => run.runId !== props.liveRunState.runId)
})

const isLoadingHistory = computed<boolean>(() => store.getters.isFactoryRunListLoading?.(props.factoryId) ?? false)

const selectedHistoricalRun = computed<FactoryRunRecord | undefined>(() => {
  if (!selectedRunId.value) return undefined
  return historicalRuns.value.find((run) => run.runId === selectedRunId.value)
})

const selectedHistoricalStepRuns = computed(() => {
  if (!selectedRunId.value) return []
  return store.getters.factoryStepRunsByRun?.(selectedRunId.value) || []
})

const selectedDisplayRun = computed<FactoryRunDisplay>(() => {
  if (selectedSource.value === 'live' && props.liveRunState.runId && props.liveRunState.run) {
    return toHistoricalFactoryRunDisplay(props.liveRunState.run, props.liveRunState.stepRuns)
  }

  if (selectedSource.value === 'live' && props.liveRunState.status === 'error' && props.liveRunState.error) {
    return toErroredFactoryRunDisplay(props.factoryId, props.factoryName, props.liveRunState.error)
  }

  if (selectedHistoricalRun.value) {
    return toHistoricalFactoryRunDisplay(selectedHistoricalRun.value, selectedHistoricalStepRuns.value)
  }

  return toIdleFactoryRunDisplay(props.factoryId, props.factoryName)
})

const liveRunSubtitle = computed(() => {
  if (!props.liveRunState.runId) {
    return props.liveRunState.error || props.liveRunState.status
  }
  return `${props.liveRunState.status}${props.liveRunState.runId ? ` • ${props.liveRunState.runId.slice(0, 8)}` : ''}`
})

const liveRunTitle = computed(() => {
  switch (props.liveRunState.status) {
    case 'completed':
      return 'Completed Run'
    case 'cancelled':
      return 'Cancelled Run'
    case 'error':
      return 'Failed Run'
    case 'paused':
      return 'Paused Run'
    case 'queued':
    case 'running':
    default:
      return 'Current Run'
  }
})

const liveRowLabel = computed(() => (
  props.liveRunState.status === 'running' || props.liveRunState.status === 'queued'
    ? 'LIVE'
    : props.liveRunState.status.toUpperCase()
))

async function fetchRunHistory() {
  await store.dispatch('fetchFactoryRunsForFactory', { factoryId: props.factoryId, limit: 20, offset: 0, append: false })
}

async function selectHistoricalRun(runId: string) {
  selectedSource.value = 'history'
  selectedRunId.value = runId
  await store.dispatch('fetchFactoryRunSteps', { runId, limit: 200, offset: 0, append: false })
}

function selectLiveRun() {
  selectedSource.value = 'live'
  selectedRunId.value = props.liveRunState.runId
}

function pickInitialSelection() {
  if (hasLiveDisplay.value) {
    selectLiveRun()
    return
  }
  const firstHistorical = historicalRuns.value[0]
  if (firstHistorical) {
    selectedSource.value = 'history'
    selectedRunId.value = firstHistorical.runId
    void store.dispatch('fetchFactoryRunSteps', { runId: firstHistorical.runId, limit: 200, offset: 0, append: false })
    return
  }
  selectedSource.value = 'history'
  selectedRunId.value = null
}

function formatRunTitle(run: FactoryRunRecord) {
  return new Date(run.startedAt).toLocaleString()
}

function formatRunSubtitle(run: FactoryRunRecord) {
  const steps = run.cursor.iterationCount
  const tokens = run.aggregateUsage?.totalTokens || 0
  return `${steps} step${steps === 1 ? '' : 's'} • ${tokens} tokens`
}

function statusColor(status: FactoryRunRecord['status'] | FactoryStreamState['status']) {
  switch (status) {
    case 'running': return 'primary'
    case 'queued': return 'grey'
    case 'paused': return 'warning'
    case 'completed': return 'success'
    case 'cancelled': return 'warning'
    case 'error': return 'error'
    default: return 'grey'
  }
}

watch(
  () => props.factoryId,
  async () => {
    selectedRunId.value = null
    selectedSource.value = 'history'
    await fetchRunHistory()
    pickInitialSelection()
  }
)

watch(
  () => props.liveRunState.runId,
  async (runId) => {
    if (!runId) return
    selectedSource.value = 'live'
    selectedRunId.value = runId
    await fetchRunHistory()
  }
)

watch(
  () => `${props.liveRunState.status}:${props.liveRunState.error || ''}`,
  () => {
    if (props.liveRunState.status === 'error' && !props.liveRunState.runId) {
      selectedSource.value = 'live'
      selectedRunId.value = null
    }
  }
)

watch(
  () => historicalRuns.value.map((run) => run.runId).join('|'),
  () => {
    if (selectedSource.value === 'history' && selectedRunId.value && historicalRuns.value.some((run) => run.runId === selectedRunId.value)) {
      return
    }
    if (selectedSource.value === 'live' && hasLiveDisplay.value) {
      return
    }
    pickInitialSelection()
  }
)

onMounted(async () => {
  await fetchRunHistory()
  pickInitialSelection()
})
</script>

<style scoped lang="scss">
.workflow-run-browser {
  display: flex;
  gap: 16px;
  height: 100%;
  min-height: 0;
}

.workflow-run-browser__history {
  width: 280px;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.workflow-run-browser__details {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}

.workflow-run-browser__list {
  overflow-y: auto;
}

.workflow-run-browser__title {
  display: flex;
  align-items: center;
}
</style>
